import { normalizeImageStyle } from '../creditCostApi.js';

const popFromRanking = (char) =>
  Number.parseInt(String(char.pop ?? char.total_messages_sent ?? 0), 10) || 0;

const characterPopularity = (char) =>
  Number.parseInt(
    String(char?.pop ?? char?.total_messages_sent ?? char?.total_popularity ?? 0),
    10
  ) || 0;

const isPrivateCharacter = (char) =>
  !!(char?.private || char?.rank === 'Private');

const numericRank = (char) => {
  if (isPrivateCharacter(char)) return Number.POSITIVE_INFINITY;
  if (char?.rank == null || char.rank === '') return Number.POSITIVE_INFINITY;
  const n = Number(char.rank);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};

export const sortCharactersByRankAsc = (characters) =>
  [...(characters || [])].sort((a, b) => {
    const aPrivate = isPrivateCharacter(a);
    const bPrivate = isPrivateCharacter(b);
    if (aPrivate !== bPrivate) return aPrivate ? 1 : -1;
    const ar = numericRank(a);
    const br = numericRank(b);
    const aHasRank = Number.isFinite(ar);
    const bHasRank = Number.isFinite(br);
    if (aHasRank && bHasRank && ar !== br) return ar - br;
    if (aHasRank !== bHasRank) return aHasRank ? -1 : 1;
    const popDiff = characterPopularity(b) - characterPopularity(a);
    if (popDiff) return popDiff;
    return (a.name || '').localeCompare(b.name || '');
  });

const mapPrivateCharacter = (char) => ({
  id: String(char.id),
  name: char.name,
  image: char.image || char.image_url || '/assets/images/kiss.webp',
  gender: char.gender ?? null,
  hasLora: Boolean(char.hasLora),
  style: normalizeImageStyle(char.style),
  pop: char.pop ?? char.popularity ?? 0,
  rank: 'Private',
  private: true,
  isN: char.isN ?? char.is_nsfw ?? false,
  is_nsfw: char.is_nsfw ?? char.isN ?? false,
  ce: char.ce ?? null,
  cen: char.cen ?? null,
});

export const mapCreateCharacters = ({ rankings, modalData, privateList = [] }) => {
  const genderById = {};
  const hasLoraById = {};
  const styleById = {};
  for (const item of modalData || []) {
    genderById[item.id] = item.gender;
    hasLoraById[item.id] = Boolean(item.hasLora);
    styleById[item.id] = normalizeImageStyle(item.style);
  }

  const ranked = Object.values(rankings || {})
    .map((char) => ({ id: String(char.id), pop: popFromRanking(char) }))
    .sort((a, b) => (b.pop || 0) - (a.pop || 0));

  const rankById = ranked.reduce((acc, item, idx) => {
    acc[item.id] = idx + 1;
    return acc;
  }, {});

  const publicChars = Object.values(rankings || {}).map((char) => ({
    id: String(char.id),
    name: char.name,
    image: char.image,
    gender: genderById[char.id] ?? null,
    hasLora: hasLoraById[char.id] ?? false,
    style: styleById[char.id] ?? 'anime',
    pop: char.pop ?? char.total_messages_sent ?? 0,
    rank: rankById[String(char.id)] ?? null,
    isN: char.isN ?? char.is_nsfw ?? false,
    is_nsfw: char.is_nsfw ?? char.isN ?? false,
    ce: char.ce ?? null,
    cen: char.cen ?? null,
  }));

  const privateChars = (privateList || []).map(mapPrivateCharacter);
  const seenIds = new Set(publicChars.map((c) => c.id));
  const merged = [...publicChars, ...privateChars.filter((c) => !seenIds.has(c.id))];
  return sortCharactersByRankAsc(merged);
};
